import React, { useState } from "react";
import { Plus, Search, Users, CheckCircle2, XCircle, Clock, ChevronLeft, ChevronRight, Layers, Upload } from "lucide-react";
import { useAttendance } from "../hooks/useAttendance";
import { AttendanceTable } from "../components/attendance/AttendanceTable";
import { MarkAttendanceModal } from "../components/attendance/MarkAttendanceModal";
import { HRStatCard } from "../components/HRStatCard";
import { ATTENDANCE_STATUS, ATTENDANCE_STATUS_LABELS } from "../constants/hrConstants";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";

function BulkMarkPanel({ employees, date, onSave, onClose, isSaving }) {
  const [statuses, setStatuses] = React.useState(() => {
    const init = {};
    employees.forEach((e) => { init[e.id] = ATTENDANCE_STATUS.PRESENT; });
    return init;
  });

  const handleSave = () => {
    const records = employees.map((e) => ({
      employeeId: e.id, employeeName: e.name,
      date, status: statuses[e.id], checkIn: "", checkOut: "", remarks: "",
    }));
    onSave(records);
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col animate-in fade-in zoom-in-95 duration-200 border border-slate-100 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 bg-slate-50/50">
          <h2 className="text-base font-bold text-slate-800">Bulk Mark Attendance — {date}</h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-200 text-slate-500 transition-colors">✕</button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-3">
          {employees.map((e, idx) => (
            <div key={e.id} className="flex items-center justify-between bg-white border border-slate-100 rounded-xl p-3 hover:border-blue-200 hover:shadow-sm transition-all group">
              <div className="flex items-center gap-3">
                <img 
                  src={`https://i.pravatar.cc/150?img=${(idx % 70) + 1}`} 
                  className="w-10 h-10 rounded-full bg-slate-200 object-cover border-2 border-white shadow-sm" 
                  alt=""
                />
                <div>
                  <p className="text-sm font-bold text-slate-800">{e.name}</p>
                  <p className="text-xs font-medium text-slate-400 mt-0.5">#{e.id}</p>
                </div>
              </div>
              <select
                className="input w-44 text-sm font-medium bg-slate-50 border-slate-200 focus:border-blue-400 transition-colors"
                value={statuses[e.id] || ATTENDANCE_STATUS.PRESENT}
                onChange={(ev) => setStatuses((p) => ({ ...p, [e.id]: ev.target.value }))}
              >
                {Object.entries(ATTENDANCE_STATUS).map(([, v]) => (
                  <option key={v} value={v}>{ATTENDANCE_STATUS_LABELS[v]}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
        <div className="px-6 py-5 border-t border-slate-100 bg-slate-50/80 flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2.5 rounded-xl font-semibold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 transition-colors">Cancel</button>
          <button onClick={handleSave} className="px-5 py-2.5 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-sm shadow-blue-200 transition-colors" disabled={isSaving}>
            {isSaving ? "Saving..." : `Mark All (${employees.length})`}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AttendanceScreen() {
  const {
    selectedDate, setSelectedDate,
    filterEmp, setFilterEmp, filterStatus, setFilterStatus,
    filtered, stats, employees, dayRecords,
    isMarkOpen, isBulkOpen, setIsBulkOpen,
    editingRecord, formData, formErrors, isSaving,
    openMark, closeMark, updateField, save, saveAll,
  } = useAttendance();

  const changeDate = (delta) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + delta);
    setSelectedDate(d.toISOString().split("T")[0]);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const data = evt.target.result;
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        if (rows.length < 2) {
          toast.error("Spreadsheet is empty or missing data rows");
          return;
        }

        // Parse headers (case insensitive, trim spaces)
        const headers = rows[0].map((h) => String(h || "").trim().toLowerCase());
        const empIdIdx = headers.findIndex((h) => h.includes("id") || h.includes("employee"));
        const statusIdx = headers.findIndex((h) => h.includes("status"));
        const checkInIdx = headers.findIndex((h) => h.includes("in"));
        const checkOutIdx = headers.findIndex((h) => h.includes("out"));
        const remarksIdx = headers.findIndex((h) => h.includes("remark"));

        if (empIdIdx === -1 || statusIdx === -1) {
          toast.error("Spreadsheet must contain at least 'Employee ID' and 'Status' columns");
          return;
        }

        const records = [];
        for (let i = 1; i < rows.length; i++) {
          const row = rows[i];
          if (!row || row.length === 0) continue;

          const employeeId = String(row[empIdIdx] || "").trim();
          const rawStatus = String(row[statusIdx] || "").toLowerCase().trim();

          if (!employeeId || !rawStatus) continue;

          // Normalize status
          let status = "present";
          if (rawStatus.includes("absent")) status = "absent";
          else if (rawStatus.includes("half")) status = "half_day";
          else if (rawStatus.includes("late")) status = "late";
          else if (rawStatus.includes("leave")) status = "on_leave";
          else if (rawStatus.includes("holiday")) status = "holiday";
          else if (rawStatus.includes("week") || rawStatus.includes("off")) status = "week_off";

          const emp = employees.find((e) => e.id === employeeId);

          records.push({
            employeeId,
            employeeName: emp?.name || `Employee ${employeeId}`,
            date: selectedDate,
            status,
            checkIn: checkInIdx !== -1 ? String(row[checkInIdx] || "").trim() : "",
            checkOut: checkOutIdx !== -1 ? String(row[checkOutIdx] || "").trim() : "",
            remarks: remarksIdx !== -1 ? String(row[remarksIdx] || "").trim() : "",
          });
        }

        if (records.length === 0) {
          toast.error("No valid records found in spreadsheet");
          return;
        }

        await saveAll(records);
        toast.success(`Successfully uploaded ${records.length} records!`);
      } catch (err) {
        console.error(err);
        toast.error("Failed to parse spreadsheet file");
      }
    };

    reader.readAsArrayBuffer(file);
    e.target.value = ""; // reset file selector input
  };

  return (
    <>
      <div className="space-y-6 animate-in fade-in duration-400 bg-slate-50 min-h-screen pb-10">
      
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <HRStatCard label="Total" value={stats.total} icon={Users} color="blue" />
        <HRStatCard label="Present" value={stats.present} icon={CheckCircle2} color="green" />
        <HRStatCard label="Absent" value={stats.absent} icon={XCircle} color="red" />
        <HRStatCard label="Half Day" value={stats.halfDay} icon={Clock} color="amber" />
        <HRStatCard label="On Leave" value={stats.onLeave} icon={Layers} color="purple" />
      </div>

      {/* Control Panel */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          
          <div className="flex items-center gap-4">
            {/* Date Navigation */}
            <div className="flex items-center gap-1 bg-slate-50 rounded-xl border border-slate-200 p-1">
              <button onClick={() => changeDate(-1)} className="p-2 rounded-lg hover:bg-white hover:shadow-sm transition-all text-slate-500">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <input
                type="date" value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="border-none outline-none bg-transparent text-sm font-bold text-slate-700 cursor-pointer px-2"
              />
              <button onClick={() => changeDate(1)} className="p-2 rounded-lg hover:bg-white hover:shadow-sm transition-all text-slate-500">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Employee Filter */}
            <div className="relative w-48 focus-within:w-64 transition-all duration-300 ease-in-out hidden sm:block">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text" className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-10 pr-4 py-2 text-sm focus:outline-none focus:border-blue-400 transition-colors"
                placeholder="Search employee..."
                value={filterEmp} onChange={(e) => setFilterEmp(e.target.value)}
              />
            </div>

            {/* Status Filter */}
            <select 
              className="w-36 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 text-sm text-slate-600 focus:outline-none focus:border-blue-400 transition-colors" 
              value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">All Status</option>
              {Object.entries(ATTENDANCE_STATUS).map(([, v]) => (
                <option key={v} value={v}>{ATTENDANCE_STATUS_LABELS[v]}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-3">
            <button 
              onClick={() => document.getElementById("spreadsheet-file-input").click()}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 hover:text-blue-600 transition-colors"
              title="Upload Excel or CSV Sheet"
            >
              <Upload className="w-4 h-4" /> <span className="hidden sm:inline">Upload</span>
            </button>
            <input
              id="spreadsheet-file-input"
              type="file"
              accept=".csv, .xlsx, .xls"
              className="hidden"
              onChange={handleFileUpload}
            />
            <button onClick={() => openMark()} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 shadow-sm shadow-blue-200 transition-colors">
              <Plus className="w-4 h-4" /> Attendance
            </button>
          </div>
        </div>


      </div>

      {/* Table - show filtered day records */}
      <AttendanceTable
        records={filtered}
        employees={employees}
        onEdit={(rec) => openMark(null, rec)}
      />
      </div>

      {/* Modals */}
      <MarkAttendanceModal
        isOpen={isMarkOpen}
        onClose={closeMark}
        employees={employees}
        formData={formData}
        formErrors={formErrors}
        onFieldChange={updateField}
        onSubmit={save}
        isSaving={isSaving}
        isEditing={Boolean(editingRecord)}
      />

      {isBulkOpen && (
        <BulkMarkPanel
          employees={employees}
          date={selectedDate}
          onSave={saveAll}
          onClose={() => setIsBulkOpen(false)}
          isSaving={isSaving}
        />
      )}
    </>
  );
}
